import { useContext, useEffect, useMemo, useState } from 'react'

import { useSelector } from 'react-redux'
import { Input, Radio, Select } from 'antd'
import {
  faAt,
  faBirthdayCake,
  faGenderless,
  faGlobe,
  faGraduationCap,
  faIdBadge,
  faLanguage,
  faListNumeric,
  faPhone,
  faUser
} from '@fortawesome/pro-solid-svg-icons'
import { isArray, isFunction, isNumber, isObject } from 'lodash'

import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import FormItem from '../../../Components/shared/Forms/FormItem'
import { EUROPEAN_DATE } from '../../../utils/momentjs'
import BirthdatePicker from '../../../Components/antd/Selects/BirthdatePicker'
import { Genders } from '../../../utils/api/student'
import { Student, StudentProperties, StudentStatusLabels, StudentValidators } from '../../../utils/entities/student'
import { Permissions } from './SearchStudentModalProvider'
import Mailing from '../../../Components/shared/Mailing'
import LocaleLanguagesSelector from '../../../Components/shared/Selectors/Languages/LanguageSelector'
import { isValid } from '../../../utils/validators'
import { getIdOrNull, isEmptyArray } from '../../../utils'
import useDebounce from '../../../hooks/useDebounce'
import SchoolSectionsSelector from '../../../Components/shared/Selectors/SchoolSectionsSelector'
import NationalitiesSelector from '../../../Components/shared/Selectors/NationalitiesSelector'
import { isInstitutionUser, isSchoolUser } from '../../../utils/roles'
import { GlobalContext } from '../../GlobalProvider'
import { getSchoolSchoolSections } from '../../../utils/api/school'

const FIELD_WIDTH = '100%'
const FIELD_STYLE = { width: FIELD_WIDTH }

const { FIRSTNAME, LASTNAME, EMAIL, GENDER, BIRTHDATE, MATRICULE, NATIONALITY, PHONE_NUMBER, LANGUAGE, NISS, SECTIONS, STATUS } = StudentProperties

export const Types = Object.freeze({
  INTERNSHIP_REQUEST: 'internship_request',
  STUDENT: 'student'
})

const StudentForm = ({ errorFields = [], data, languages, permission, type = Types.INTERNSHIP_REQUEST, onChange, onEmailChange }) => {
  const { user } = useContext(GlobalContext)

  const [schoolSections, setSchoolSections] = useState([])

  const school = useMemo(() => data?.sections[0]?.school, [data?.sections[0]?.school])

  useEffect(() => {
    if (isObject(school)) {
      getSchoolSchoolSections(user, school).then(({ data }) => setSchoolSections(data[school.id]))
    }
  }, [school, user])

  const t = useSelector(selectTranslate)

  const isDisabled = field => (
    permission === Permissions.READ_ONLY ||
    (permission === Permissions.MATRICULE_RESTRICTED && field === MATRICULE) ||
    (permission === Permissions.EMAIL_AND_MATRICULE_RESTRICTED && (field === MATRICULE || field === EMAIL)) ||
    (permission === Permissions.FILL && data[field] !== null)
  )

  const handleChange = key => value => onChange(new Student({ ...data, [key]: value }))
  const handleEventChange = key => e => onChange(new Student({ ...data, [key]: e.target.value }))
  const handleLanguageChange = languageTag => onChange(new Student({ ...data, [LANGUAGE]: languageTag }))
  const handleSectionChange = section => onChange(new Student({ ...data, [SECTIONS]: [section] }))

  const debounced = useDebounce(onEmailChange, 1500)

  const handleEmailChange = event => {
    const email = event.target.value

    if (isFunction(debounced) && isEmptyArray(isValid({ email }, [StudentValidators.email]).keys)) {
      debounced(email)
    }

    handleChange(EMAIL)(email)
  }

  const className = field => errorFields.includes(field) ? 'error-ant-border' : ''

  const required = permission !== Permissions.READ_ONLY

  const currentSection = useMemo(() => {
    let section = null

    if (isSchoolUser(user) && isObject(data) && isArray(data.sections)) {
      section = data.sections.find(s => s.school?.id === user.school?.id || isNumber(s))
    } else if (isObject(data) && isArray(data.sections)) {
      section = data.sections[0]
    }

    return section
  }, [data?.sections, user.id])

  return (
    <>
      <FormItem icon={faAt} label='form.label.school_email' required={required}>
        {permission === Permissions.READ_ONLY || permission === Permissions.EMAIL_AND_MATRICULE_RESTRICTED ? (
          <div className='pdl-8 fake-gray-input w-100'>
            <Mailing email={data[EMAIL]} />
          </div>
        ) : (
          <Input
            className={className(EMAIL)}
            disabled={isDisabled(EMAIL)}
            style={FIELD_STYLE}
            value={data[EMAIL]}
            onChange={handleEmailChange}
          />
        )}
      </FormItem>
      {type === Types.STUDENT && (
        <FormItem icon={faGraduationCap} label='form.label.section' className={className(SECTIONS)} required>
          <SchoolSectionsSelector
            disabled={isDisabled(SECTIONS)}
            style={{ width: '100%' }}
            values={getIdOrNull(currentSection)}
            schoolSections={isInstitutionUser(user) ? schoolSections : null}
            onChange={handleSectionChange}
          />
        </FormItem>
      )}
      {type === Types.STUDENT && (
        <FormItem icon={faListNumeric} label='form.label.matricule' className={className(MATRICULE)}>
          <Input
            className={className(MATRICULE)}
            disabled={isDisabled(MATRICULE)}
            style={FIELD_STYLE}
            value={data[MATRICULE]}
            onChange={handleEventChange(MATRICULE)}
          />
        </FormItem>
      )}
      {type === Types.STUDENT && (
        <FormItem icon={faGlobe} label='form.label.nationality' className={className(NATIONALITY)}>
          <NationalitiesSelector
            disabled={isDisabled(NATIONALITY)}
            showSearch
            style={{ width: '100%' }}
            values={data[NATIONALITY]}
            onChange={handleChange(NATIONALITY)}
          />
        </FormItem>
      )}
      {type === Types.STUDENT && (
        <FormItem icon={faGraduationCap} label='common.status' className={className(STATUS)}>
          <Select
            disabled={isDisabled(STATUS)}
            style={FIELD_STYLE}
            value={data[STATUS]}
            onChange={handleChange(STATUS)}
          >
            {StudentStatusLabels.map(status => (
              <Select.Option key={status} value={status}> {t('student_status_codes.' + status.toLowerCase())} </Select.Option>
            ))}
          </Select>
        </FormItem>
      )}
      <FormItem icon={faGenderless} label='form.label.gender' className={className(GENDER)}>
        <Radio.Group value={data[GENDER]} buttonStyle='solid' onChange={handleEventChange(GENDER)} disabled={isDisabled(GENDER)}>
          {Object.keys(Genders).map(k => (
            <Radio.Button key={k} value={Genders[k]}> {t('genders.' + k.toLowerCase())} </Radio.Button>
          ))}
        </Radio.Group>
      </FormItem>
      <div className='flex-row w-100'>
        <FormItem className='w-50 pdr-4' icon={faUser} label='form.label.firstname' required={required}>
          <Input
            className={className(FIRSTNAME)}
            disabled={isDisabled(FIRSTNAME)}
            style={FIELD_STYLE}
            value={data[FIRSTNAME]}
            onChange={handleEventChange(FIRSTNAME)}
          />
        </FormItem>
        <FormItem className='w-50 pdl-4' icon={faUser} label='form.label.lastname' required={required}>
          <Input
            className={className(LASTNAME)}
            disabled={isDisabled(LASTNAME)}
            style={FIELD_STYLE}
            value={data[LASTNAME]}
            onChange={handleEventChange(LASTNAME)}
          />
        </FormItem>
      </div>
      <FormItem icon={faIdBadge} label='form.label.niss' info='form.label.niss_info'>
        <Input
          className={className(NISS)}
          disabled={isDisabled(NISS)}
          style={FIELD_STYLE}
          value={data[NISS]}
          onChange={handleEventChange(NISS)}
        />
      </FormItem>
      <div className='flex-row w-100'>
        <FormItem className={'w-50 pdr-4 ' + className(LANGUAGE)} icon={faLanguage} label='form.label.language' info={t('internship_request_from.info.language')} required={required}>
          <LocaleLanguagesSelector
            disabled={isDisabled(LANGUAGE)}
            languages={languages}
            style={FIELD_STYLE}
            values={data[LANGUAGE]}
            onChange={handleLanguageChange}
          />
        </FormItem>
        <FormItem className='w-50 pdl-4' icon={faPhone} label='form.label.phone_number'>
          <Input value={data[PHONE_NUMBER]} onChange={handleEventChange(PHONE_NUMBER)} disabled={isDisabled(PHONE_NUMBER)} />
        </FormItem>
      </div>
      <FormItem icon={faBirthdayCake} label='form.label.birthdate'>
        <BirthdatePicker value={data[BIRTHDATE]?.format(EUROPEAN_DATE)} onChange={handleChange(BIRTHDATE)} disabled={isDisabled(BIRTHDATE)} />
      </FormItem>
    </>
  )
}

export default StudentForm
